// Keep the canvas coordinate space stable while upgrading a static preview.
export class MapBackground {
  constructor(url, type, { redraw, failed, previewUrl, upgraded, width, height }) {
    Object.assign(this, { url, type, redraw, failed, previewUrl, upgraded });
    this.originalWidth = width;
    this.originalHeight = height;
    this.abort = new AbortController();
    this.started = false;
    this.disposed = false;
  }

  async load() {
    if (this.type === 'image/gif') {
      const response = await fetch(this.url, { signal: this.abort.signal });
      if (!response.ok) throw new Error('Map download failed');
      const bytes = await response.arrayBuffer();
      if (this.disposed) return;
      this.image = document.createElement('canvas');
      this.worker = new Worker(new URL('./gif_worker.js', import.meta.url), { type: 'module' });
      this.displayGifFrame(await this.gifFrame(bytes));
    } else if (!this.type.startsWith('video/')) {
      if (this.previewUrl && this.originalWidth > 0 && this.originalHeight > 0) {
        try {
          this.image = await this.loadStill(this.previewUrl);
          this.usingPreview = true;
        } catch (error) {
          if (this.disposed) throw error;
        }
      }
      if (!this.image) this.image = await this.loadStill(this.url);
    } else {
      const video = this.type.startsWith('video/');
      this.animated = video;
      this.image = video ? document.createElement('video') : new Image();
      this.image.crossOrigin = 'anonymous';
      if (video) {
        this.image.muted = true;
        this.image.defaultMuted = true;
        this.image.loop = true;
        this.image.playsInline = true;
        this.image.preload = 'auto';
      }
      await new Promise((resolve, reject) => {
        this.image.addEventListener(video ? 'loadeddata' : 'load', resolve, { once: true, signal: this.abort.signal });
        this.image.addEventListener('error', () => reject(new Error('Unsupported or unavailable map')), {
          once: true,
          signal: this.abort.signal
        });
        this.abort.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {
          once: true
        });
        this.image.src = this.url;
      });
      if (video) this.image.addEventListener('error', () => this.fail(), { signal: this.abort.signal });
    }
    this.width = this.usingPreview ? this.originalWidth : this.image.videoWidth || this.image.naturalWidth || this.image.width;
    this.height = this.usingPreview ? this.originalHeight : this.image.videoHeight || this.image.naturalHeight || this.image.height;
    if (!this.width || !this.height) throw new Error('Map has no dimensions');
    return this.image;
  }

  async loadStill(url) {
    const image = new Image();
    this.pendingImage = image;
    image.crossOrigin = 'anonymous';
    try {
      await new Promise((resolve, reject) => {
        const cleanup = () => {
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          this.abort.signal.removeEventListener('abort', aborted);
        };
        const loaded = () => {
          cleanup();
          resolve();
        };
        const failed = () => {
          cleanup();
          reject(new Error('Map download failed'));
        };
        const aborted = () => {
          cleanup();
          reject(new DOMException('Aborted', 'AbortError'));
        };
        if (this.disposed) return aborted();
        image.addEventListener('load', loaded);
        image.addEventListener('error', failed);
        this.abort.signal.addEventListener('abort', aborted, { once: true });
        image.src = url;
      });
      await image.decode();
      if (this.disposed) throw new DOMException('Aborted', 'AbortError');
      if (!image.naturalWidth || !image.naturalHeight) throw new Error('Map has no dimensions');
      return image;
    } catch (error) {
      image.removeAttribute('src');
      throw error;
    } finally {
      if (this.pendingImage === image) this.pendingImage = null;
    }
  }

  async upgrade() {
    if (!this.usingPreview || this.upgradeStarted || this.disposed) return;
    this.upgradeStarted = true;
    try {
      const original = await this.loadStill(this.url);
      if (this.disposed) return;
      // The preview is stretched into the original's oriented coordinate space.
      if (original.naturalWidth !== this.width || original.naturalHeight !== this.height) {
        original.removeAttribute('src');
        return;
      }
      const preview = this.image;
      this.image = original;
      this.usingPreview = false;
      this.upgraded?.(original);
      preview.removeAttribute('src');
    } catch {
      // A failed upgrade leaves the already working preview on the canvas.
    }
  }

  gifFrame(bytes) {
    return new Promise((resolve, reject) => {
      this.rejectFrame = reject;
      // A stalled decoder must not retain a worker indefinitely. This limits work
      // on a single frame, never the upload's duration, resolution, or frame count.
      this.decodeTimeout = setTimeout(() => reject(new Error('GIF decoding timed out')), 30000);
      this.worker.onerror = () => reject(new Error('GIF decoding failed'));
      this.worker.onmessage = ({ data }) => {
        clearTimeout(this.decodeTimeout);
        this.rejectFrame = null;
        if (this.disposed) {
          data.bitmap?.close();
          return;
        }
        if (data.error) {
          reject(new Error(data.error));
          return;
        }
        resolve(data);
      };
      this.worker.postMessage(bytes ? { bytes } : {}, bytes ? [bytes] : []);
    });
  }

  async start() {
    if (!this.animated) return this.upgrade();
    if (this.disposed || this.started || !this.animated) return;
    this.started = true;
    const playback = (this.playback = (this.playback || 0) + 1);
    try {
      if (this.worker) {
        this.frameDeadline = performance.now() + this.frameDelay;
        if (this.nextGifFrame) this.scheduleGifFrame();
        else if (!this.decodingGif) this.tickGif();
      } else {
        await this.image.play();
        if (!this.disposed && this.started && playback === this.playback) this.tickVideo();
      }
    } catch {
      if (!this.disposed && this.started && playback === this.playback) this.fail();
    }
  }

  displayGifFrame(data) {
    this.image.width = data.bitmap.width;
    this.image.height = data.bitmap.height;
    this.image.getContext('2d').drawImage(data.bitmap, 0, 0);
    data.bitmap.close();
    this.frameDelay = data.delay;
    this.frameDeadline = performance.now() + data.delay;
    this.animated = data.animated;
  }

  async tickGif() {
    this.decodingGif = true;
    try {
      // Decode one frame ahead while the current frame is being displayed.
      this.nextGifFrame = await this.gifFrame();
      this.decodingGif = false;
      if (this.disposed) {
        this.nextGifFrame.bitmap?.close();
        this.nextGifFrame = null;
        return;
      }
      if (this.started) this.scheduleGifFrame();
    } catch {
      this.decodingGif = false;
      if (!this.disposed) this.fail();
    }
  }

  scheduleGifFrame() {
    this.timer = setTimeout(
      () => {
        this.displayGifFrame(this.nextGifFrame);
        this.nextGifFrame = null;
        this.redraw();
        this.tickGif();
      },
      Math.max(0, this.frameDeadline - performance.now())
    );
  }

  pause() {
    this.started = false;
    this.playback = (this.playback || 0) + 1;
    clearTimeout(this.timer);
    cancelAnimationFrame(this.animationFrame);
    this.image?.cancelVideoFrameCallback?.(this.videoFrame);
    this.image?.pause?.();
  }

  tickVideo() {
    if (this.disposed || !this.started) return;
    this.redraw();
    if (this.image.requestVideoFrameCallback) {
      this.videoFrame = this.image.requestVideoFrameCallback(() => this.tickVideo());
    } else {
      this.animationFrame = requestAnimationFrame(() => this.tickVideo());
    }
  }

  fail() {
    this.dispose();
    this.failed();
  }

  dispose() {
    if (this.disposed) return;
    this.pause();
    this.disposed = true;
    this.abort.abort();
    this.pendingImage?.removeAttribute('src');
    this.pendingImage = null;
    clearTimeout(this.decodeTimeout);
    this.rejectFrame?.(new DOMException('Aborted', 'AbortError'));
    this.worker?.terminate();
    this.worker = null;
    this.nextGifFrame?.bitmap?.close();
    this.nextGifFrame = null;
    if (this.image?.tagName === 'VIDEO') {
      this.image.removeAttribute('src');
      this.image.load();
    } else if (this.image?.tagName === 'IMG') {
      this.image.removeAttribute('src');
    } else if (this.image) {
      this.image.width = this.image.height = 0;
    }
    this.image = null;
  }
}
